import React, { useState } from 'react';
import { Container, Paper, Typography, Button, Alert, Box, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import documentService from '../services/documentService';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [jobId, setJobId] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setProgress(0);
    setMessage('');
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await documentService.uploadDocument(file);
      
      clearInterval(progressInterval);
      setProgress(100);
      setJobId(result.job_id);
      setMessage(`Success! Job ID: ${result.job_id}`);
      
      // Navigate to document view after 2 seconds
      setTimeout(() => {
        navigate(`/documents/${result.job_id}`);
      }, 2000);
      
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Upload Document</Typography>
      
      <Paper sx={{ p: 4, mt: 3 }}>
        <Box
          sx={{
            border: '2px dashed',
            borderColor: file ? 'success.main' : 'grey.400',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: file ? 'success.50' : 'grey.50',
            cursor: 'pointer',
            mb: 3,
          }}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
            style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={uploading}
          />
          
          {file ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h6">{file.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6">Drag and drop or click to browse</Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: PDF, JPG, PNG, TIFF
              </Typography>
            </>
          )}
        </Box>

        {uploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Uploading... {progress}%
            </Typography>
          </Box>
        )}

        {message && (
          <Alert 
            severity={message.includes('Success') ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {message}
            {jobId && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Redirecting to document view...
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setFile(null)}
            disabled={!file || uploading}
          >
            Clear
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Upload;